import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';

const SSR_SERVER_EXAMPLE = `// server.ts — Express SSR with onefold
import express from 'express';
import { html } from 'onefold';
import { renderHTML } from 'onefold/ssr';

const app = express();
app.use('/public', express.static('dist/public'));

// Static page — pure server render, no client JS needed
app.get('/', (req, res) => {
  const body = renderHTML(() => html\`
    <div>
      <h1>Welcome</h1>
      <p>This content is fully server-rendered.</p>
    </div>
  \`);
  res.send(shell('Home', body));
});

// Async page — fetch data on server, render to HTML
app.get('/users', async (req, res) => {
  const body = await renderHTML(async () => {
    const users = await db.getUsers();
    return html\`
      <ul>
        \${users.map(u => html\`<li>\${u.name} — \${u.role}</li>\`)}
      </ul>
    \`;
  });
  res.send(shell('Users', body));
});

// Interactive page — server renders shell, client mounts live component
app.get('/counter', (req, res) => {
  const body = renderHTML(() => html\`
    <div>
      <h1>Counter</h1>
      <div id="interactive">Loading...</div>
    </div>
  \`);
  res.send(shell('Counter', body));
});

function shell(title, body) {
  return \`<!DOCTYPE html>
<html>
<head><title>\${title}</title></head>
<body>
  <div id="app">\${body}</div>
  <script type="module" src="/public/app.js"></script>
</body>
</html>\`;
}

app.listen(3000);`;

const SSR_CLIENT_EXAMPLE = `// client.ts — selective hydration
import { mount, createSignal, html } from 'onefold';

const path = window.location.pathname;
const root = document.getElementById('interactive');

// Only mount on pages that need interactivity
if (path === '/counter' && root) {
  const count = createSignal(0);
  mount(html\`
    <div>
      <h2>\${() => count()}</h2>
      <button onclick=\${() => count.set(n => n + 1)}>+</button>
    </div>
  \`, root);
}

// Static pages (/, /users): no JS runs. Server HTML stays as-is.`;

const SSR_BUILD_EXAMPLE = `// build.mjs
import { build } from 'esbuild';
import { mkdirSync } from 'node:fs';

mkdirSync('dist/public', { recursive: true });

// Server bundle (Node.js)
await build({
  entryPoints: ['src/server.ts'],
  bundle: true,
  format: 'esm',
  platform: 'node',
  outfile: 'dist/server.mjs',
  packages: 'external',
});

// Client bundle (browser)
await build({
  entryPoints: ['src/client.ts'],
  bundle: true,
  format: 'esm',
  outfile: 'dist/public/app.js',
  minify: true,
});

console.log('dist/server.mjs  — run with: node dist/server.mjs');
console.log('dist/public/app.js — loaded by browser');`;

const SSR_PROJECT_STRUCTURE = `src/
  shared/              Shared between server + client (zero duplication)
    components/Nav.ts  Navigation bar
    layouts/Page.ts    Page wrapper
    types.ts           Interfaces
  pages/               Page components (rendered by server)
    HomePage.ts        Static — no client JS
    UsersPage.ts       Static + async data fetch
    CounterPage.ts     Interactive — client mounts into #interactive
  server/              Server-only code
    index.ts           Express app
    routes.ts          Route definitions
    data.ts            Database / API calls
  client/              Client-only code
    index.ts           Selective hydration
dist/                  Build output (deployable)
  server.mjs           Node.js server
  public/app.js        Client bundle`;

export function SsrPage(): Node {
  return html`
    <div>
      <h1>Server-Side Rendering</h1>
      <p><code>renderHTML</code> converts onefold components to HTML strings on the server. Zero dependencies. No jsdom. Fully tree-shakable.</p>

      <h2>How It Works</h2>
      ${CodeBlock(`import { html } from 'onefold';
import { renderHTML } from 'onefold/ssr';

// Sync render
const result = renderHTML(() => html\`<h1>Hello</h1>\`);
// → '<h1>Hello</h1>'

// Async render (with data fetching)
const result = await renderHTML(async () => {
  const data = await fetch('/api/users').then(r => r.json());
  return html\`<ul>\${data.map(u => html\`<li>\${u.name}</li>\`)}</ul>\`;
});`)}

      <p>Uses the same tokenizer as client-side <code>html</code>. Reactive expressions evaluate once. Event handlers are stripped. Same XSS escaping applies.</p>

      ${Callout('renderHTML is tree-shakable. If your client bundle never imports it, it adds 0 bytes. Only server code pays for it.')}

      <h2>Properties</h2>
      <table>
        <tr><th>Property</th><th>Value</th></tr>
        <tr><td>Dependencies</td><td>Zero (no jsdom, no DOM polyfill)</td></tr>
        <tr><td>Performance</td><td>~0.5ms per page</td></tr>
        <tr><td>Tree-shakable</td><td>0 bytes in client bundle if not imported</td></tr>
        <tr><td>Async support</td><td>Yes — accepts async component functions</td></tr>
        <tr><td>Security</td><td>Same escaping: HTML entities, URL scheme blocking, event stripping</td></tr>
        <tr><td>Per-page opt-in</td><td>SSR whichever routes you want, skip the rest</td></tr>
      </table>

      <h2>The Pattern</h2>
      <p>onefold SSR uses <strong>selective hydration</strong>:</p>
      <ul>
        <li><strong>Static pages</strong> — server renders full HTML. Client does nothing. Zero JS overhead.</li>
        <li><strong>Interactive pages</strong> — server renders a shell (nav, title, placeholder). Client mounts a live component into the placeholder.</li>
      </ul>

      <table>
        <tr><th>Route</th><th>Server</th><th>Client</th></tr>
        <tr><td><code>/</code></td><td>Renders full content</td><td>No JS runs</td></tr>
        <tr><td><code>/users</code></td><td>Fetches data, renders HTML</td><td>No JS runs</td></tr>
        <tr><td><code>/counter</code></td><td>Renders shell with <code>#interactive</code></td><td>Mounts live counter</td></tr>
      </table>

      <h2>Server Example</h2>
      ${CodeBlock(SSR_SERVER_EXAMPLE)}

      <h2>Client Example</h2>
      ${CodeBlock(SSR_CLIENT_EXAMPLE)}

      <h2>Build Script</h2>
      <p>Use esbuild to produce both server and client bundles:</p>
      ${CodeBlock(SSR_BUILD_EXAMPLE)}

      <h2>Project Structure</h2>
      <p>Recommended layout for scalable SSR apps with zero code duplication:</p>
      ${CodeBlock(SSR_PROJECT_STRUCTURE)}

      <h2>What Gets Stripped in SSR Output</h2>
      <table>
        <tr><th>Feature</th><th>In HTML output?</th><th>Why</th></tr>
        <tr><td>Text content</td><td>Yes (escaped)</td><td>Content is the point of SSR</td></tr>
        <tr><td>Attributes (class, style, href)</td><td>Yes (escaped)</td><td>Styling + structure</td></tr>
        <tr><td>Event handlers (onclick)</td><td>Stripped</td><td>Can't execute in static HTML</td></tr>
        <tr><td>ref callbacks</td><td>Stripped</td><td>No DOM node on server</td></tr>
        <tr><td>Reactive expressions</td><td>Evaluated once</td><td>Snapshot of current signal value</td></tr>
        <tr><td>Unsafe URLs (javascript:)</td><td>Blocked</td><td>Same security as client</td></tr>
      </table>

      <h2>Deploy</h2>
      ${CodeBlock(`# Build
npm run build

# Run
node dist/server.mjs

# Or with PORT env
PORT=8080 node dist/server.mjs`)}

      <p>The <code>dist/</code> folder is self-contained — deploy to any Node.js host (Railway, Render, Fly.io, VPS).</p>

      <h2>When to Use SSR</h2>
      <table>
        <tr><th>Use SSR</th><th>Skip SSR (client-only SPA)</th></tr>
        <tr><td>Landing pages, marketing sites</td><td>Dashboards, admin panels</td></tr>
        <tr><td>Blog posts, documentation</td><td>Behind-login features</td></tr>
        <tr><td>E-commerce product pages</td><td>Real-time collaborative apps</td></tr>
        <tr><td>Social media link previews needed</td><td>Heavy client-side interaction</td></tr>
        <tr><td>SEO matters</td><td>Content is user-specific anyway</td></tr>
      </table>

      <h2>API</h2>
      ${raw(`<table>
        <tr><th>Function</th><th>Signature</th><th>Description</th></tr>
        <tr><td><code>renderHTML</code></td><td><code>(() =&gt; unknown) =&gt; string</code></td><td>Sync render — returns HTML string immediately</td></tr>
        <tr><td><code>renderHTML</code></td><td><code>(() =&gt; Promise) =&gt; Promise&lt;string&gt;</code></td><td>Async render — waits for data, then returns HTML string</td></tr>
      </table>`)}

      <h2>Run the Example</h2>
      ${CodeBlock(`# Full working SSR app is in examples/ssr-app/
cd examples/ssr-app
npm install
npm run build
npm start
# → http://localhost:3000

# Routes:
#   /          Static (SSR only)
#   /about     Static (SSR only)
#   /users     Static + async data fetch
#   /counter   Interactive (client mounts)
#   /todo      Interactive (client mounts)
#   /search    Interactive (client mounts)`)}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/core/templates">Templates</a> — same components work on server and client</li>
        <li><a href="/data/resource">Resource</a> — async data fetching patterns</li>
        <li><a href="/routing/router">Router</a> — client-side navigation after hydration</li>
        <li><a href="/performance/code-splitting">Code Splitting</a> — reduce client bundle size</li>
      </ul>
    </div>
  `;
}
